// @flow
import * as React from 'react';
import {
  Button,
  Modal,
  Spin,
  Input,
  Checkbox,
  DatePicker,
  Popconfirm,
  Typography,
} from 'antd';
import FilesManager from '../filesManager/FilesManager';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  createMedicalProfileResourceItem,
  updateMedicalProfileResourceItem,
  deleteMedicalProfileResourceItem,
} from '../../store/slices/medicalProfileSlice';
import { useFilesManagerHandler } from '../../hooks/useFilesManagerHandler';
import { useInputState } from '../../hooks/useInputState';
import { removeKeysWithUndefinedValue } from '../../utils';
import message from '../../utils/message';
import moment from 'moment';
import type {
  MPMedicalHistory,
  MedicalProfileResource,
} from '../../config/types-mp';
import EditRow from '../editRow/EditRow';
import { unwrapResult } from '@reduxjs/toolkit';

type Props = {
  visible: boolean,
  consultationId?: number,
  dataItem?: MPMedicalHistory,
  readOnly?: boolean,
  title?: string,
  onCloseModal: Function,
};

const { Text } = Typography;
const { TextArea } = Input;

const MPEditMedicalHistoryItemScreen = (props: Props) => {
  if (props?.title == null) {
    console.warn(
      'MPEditMedicalHistoryItemScreen expects "title" to be passed as navigation parameters',
    );
  }
  const { t } = useTranslation();
  const resource: MedicalProfileResource = 'medical_history';
  const dataItem: MPMedicalHistory = props?.dataItem || {};
  const readOnly: boolean = Boolean(props?.readOnly);
  const consultation_id: ?number = props?.consultationId;
  const patient_id = useSelector((state) => state.profile.user.id);

  const dispatch = useDispatch();

  const notesInput = React.useRef(null);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [savingErrorMeta, setSavingErrorMeta] = React.useState({});
  const {
    files,
    onFilesUpdated,
    continueWithFilesUploadCaution,
  } = useFilesManagerHandler(dataItem.files);

  // HISTORY ITEM PROPERTY related --- text props
  const notesState = useInputState(dataItem.notes);
  const nameState = useInputState(dataItem.name);

  // HISTORY ITEM PROPERTY related --- ongoing
  const [ongoing, setOngoing] = React.useState(dataItem.ongoing);

  // HISTORY ITEM PROPERTY related --- date
  let historyItemDate;
  if (dataItem.date != null) {
    historyItemDate = moment(dataItem.date).toDate();
  } else {
    historyItemDate = new Date();
  }
  const [date, setDate] = React.useState(historyItemDate);
  const now = new Date();
  const hundrendYearsAgo = new Date(
    now.getFullYear() - 100,
    now.getMonth(),
    now.getDate(),
  );

  const onSave = React.useCallback(async () => {
    const data = removeKeysWithUndefinedValue({
      name: nameState.value === '' ? null : nameState.value,
      date: date ? moment(date).format() : undefined,
      ongoing,
      notes: notesState.value === '' ? null : notesState.value,
      files,
    });

    if (Object.keys(data).length === 0) {
      // ok, there is no change..
      // just go back

    } else {
      try {
        setSaving(true);
        setSavingErrorMeta({});
        if (dataItem.id) {
          await dispatch(
            updateMedicalProfileResourceItem({
              patient_id,
              consultation_id,
              itemId: dataItem.id,
              data,
              resource,
            }),
          ).then(unwrapResult);
        } else {
          await dispatch(
            createMedicalProfileResourceItem({
              patient_id,
              consultation_id,
              data,
              resource,
            }),
          ).then(unwrapResult);
        }
        props.onCloseModal(false);
      } catch (error) {
        setSaving(false);
        setSavingErrorMeta(error.meta);
        message.error(error.message);
      }
    }
  }, [
    notesState,
    nameState,
    date,
    ongoing,
    files,
    patient_id,
    consultation_id,
    dataItem,
    resource,
    t,
    dispatch,
  ]);

  const trySave = React.useCallback(() => {
    continueWithFilesUploadCaution()
      .then(onSave)
      .catch((_) => console.log('user cancelled'));
  }, [continueWithFilesUploadCaution, onSave]);

  const onDelete = () => {
    try {
      setDeleting(true);
      if (dataItem.id != null) {
        dispatch(
          deleteMedicalProfileResourceItem({
            patient_id,
            consultation_id,
            itemId: dataItem.id,
            resource,
          }),
        ).then(unwrapResult)
          .then(() => {
            props.onCloseModal(false);
          });
      }
    } catch (error) {
      setDeleting(false);
      message.error(error.message);
    }
  };

  return (
    <Modal
      closable={false}
      destroyOnClose={true}
      maskClosable={false}
      centered={true}
      title={t('Medical history')}
      className={'profile-modal'}
      bodyStyle={styles.modalBackground}
      okText={t('Save')}
      confirmLoading={saving}
      cancelText={t('Cancel')}
      onOk={trySave}
      onCancel={() => props.onCloseModal(!props.visible)}
      visible={props.visible}
    >
      <EditRow text={t('Name')} value={nameState.value}>
        {!readOnly ? (
          <Input
            disabled={readOnly}
            style={styles.input}
            value={nameState.value}
            maxLength={255}
            status={savingErrorMeta.name != null ? 'danger' : null}
            caption={
              savingErrorMeta.name != null
                ? t('This field is required')
                : null
            }
            {...nameState}
          />
        ) : null}
      </EditRow>
      <EditRow
        text={t('Date', { context: 'medical profile measurement' })}
        value={moment(date).format('DD MMM YYYY')}>
        {!readOnly ? (
          <DatePicker
            className={'fullWidth'}
            placeholder={moment(date).format('ddd, DD MMM YYYY')}
            format={'ddd, DD MMM YYYY'}
            disabled={readOnly}
            onSelect={(nextDate) => setDate(nextDate)}
            min={hundrendYearsAgo}
            max={now}
          />
        ) : null}
      </EditRow>
      <EditRow
        text={t('Ongoing', {
          context: 'medical profile medical history',
        })}
        value={ongoing ? t('Yes') : t('No')}>
        {!readOnly ? (
          <Checkbox
            checked={ongoing}
            onChange={(nextChecked) => setOngoing(nextChecked.target.checked)}
            style={styles.checkbox}
          />
        ) : null}
      </EditRow>
      <EditRow text={t('Notes')} value={notesState.value}>
        {!readOnly ? (
          <TextArea
            ref={notesInput}
            disabled={readOnly}
            rows={4}
            style={styles.input}
            status={savingErrorMeta.notes != null ? 'danger' : null}
            caption={
              savingErrorMeta.notes != null
                ? t('This field is required')
                : null
            }
            {...notesState}
          />
        ) : null}
      </EditRow>
      <EditRow text={t('Attachments')}>
        <FilesManager
          autoUploadNewFiles={true}
          editable={!readOnly}
          files={dataItem.files}
          onFilesUpdated={onFilesUpdated}
        />
      </EditRow>
      {dataItem.id != null && !readOnly ? (
        <Popconfirm
          placement="bottom"
          title={t('Are you sure you want to delete this entry?')}
          onConfirm={onDelete}
          okText={t('Yes')}
          cancelText={t('No')}>
          <Button
            loading={deleting}
            size={'middle'}
            style={styles.deleteButton}
          >
            {t('Delete entry')}
          </Button>
        </Popconfirm>
      ) : null}
    </Modal>
  );
};

const styles = {
  container: {
    flex: 1,
    paddingLeft: 16,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    textAlign: 'center',
  },
  input: {
    backgroundColor: '#ffffff',
  },
  inputText: {},
  notesText: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  checkbox: {
    alignSelf: 'flex-start',
  },
  deleteButton: {
    alignSelf: 'center',
    marginVertical: 24,
  },
  modalBackground: {
    backgroundColor: 'rgba(72, 72, 72, 0.1)'
  }
};

export default MPEditMedicalHistoryItemScreen;
